import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Banknote,
  BookOpen,
  Briefcase,
  CalendarCheck,
  CalendarOff,
  CheckSquare,
  Clock,
  PlusCircle,
  UserPlus,
  Users,
  Wallet,
  Zap,
} from 'lucide-react';
import type { PermissionCode } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import { usePermission } from '@/hooks/usePermission';
import { Card, CardContent } from '@/components/ui/card';
import { SectionHeader } from '@/components/ui/section-header';
import { Button } from '@/components/ui/button';

interface QuickActionsWidgetProps {
  isAdminTier: boolean;
  isManagerTier: boolean;
}

interface QuickAction {
  label: string;
  href: string;
  icon: LucideIcon;
  permission?: PermissionCode;
}

const ADMIN_ACTIONS: QuickAction[] = [
  { label: 'Add Staff', href: '/hrms/staff/new', icon: UserPlus, permission: PERMISSIONS.STAFF_CREATE },
  { label: 'Approve Leave', href: '/hrms/leave', icon: CalendarCheck, permission: PERMISSIONS.LEAVE_APPROVE },
  { label: 'Process Payroll', href: '/hrms/payroll', icon: Banknote, permission: PERMISSIONS.SALARY_PROCESS },
  { label: 'View Attendance', href: '/hrms/attendance', icon: Clock, permission: PERMISSIONS.ATTENDANCE_VIEW_ALL },
  { label: 'Manage SOP', href: '/hrms/sop', icon: BookOpen, permission: PERMISSIONS.SOP_MANAGE },
  { label: 'Recruitment', href: '/hrms/recruitment', icon: Briefcase, permission: PERMISSIONS.RECRUITMENT_VIEW },
];

const MANAGER_ACTIONS: QuickAction[] = [
  { label: 'Approve Leave', href: '/hrms/leave', icon: CalendarCheck, permission: PERMISSIONS.LEAVE_APPROVE },
  { label: 'View Team', href: '/hrms/staff/list', icon: Users, permission: PERMISSIONS.STAFF_VIEW_TEAM },
  { label: 'Assign Task', href: '/tasks', icon: CheckSquare, permission: PERMISSIONS.TASK_ASSIGN },
  { label: 'Team Attendance', href: '/hrms/attendance', icon: Clock, permission: PERMISSIONS.ATTENDANCE_VIEW_TEAM },
];

const USER_ACTIONS: QuickAction[] = [
  { label: 'Apply Leave', href: '/leave', icon: CalendarOff },
  { label: 'My Salary', href: '/account/salary', icon: Banknote, permission: PERMISSIONS.SALARY_VIEW_OWN },
  { label: 'My Attendance', href: '/account/attendance', icon: Clock },
  { label: 'Reimbursement', href: '/reimbursement', icon: Wallet, permission: PERMISSIONS.FINANCE_REIMBURSEMENT_REQUEST },
  { label: 'Log Work', href: '/additional-task', icon: PlusCircle },
];

function gridClass(count: number): string {
  if (count <= 2) return 'grid-cols-2';
  if (count === 3) return 'grid-cols-3';
  if (count === 4) return 'grid-cols-2 sm:grid-cols-4';
  if (count === 5) return 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5';
  return 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6';
}

export function QuickActionsWidget({ isAdminTier, isManagerTier }: QuickActionsWidgetProps) {
  const { has } = usePermission();

  const actions = isAdminTier ? ADMIN_ACTIONS : isManagerTier ? MANAGER_ACTIONS : USER_ACTIONS;

  const visibleActions = actions.filter((a) => !a.permission || has(a.permission));

  if (visibleActions.length === 0) return null;

  return (
    <Card className="shadow-card">
      <SectionHeader icon={<Zap />} title="Quick Actions" hint="Common tasks for your role" />
      <CardContent className="p-4">
        <div className={`grid gap-3 ${gridClass(visibleActions.length)}`}>
          {visibleActions.map((a) => (
            <Link key={a.href + a.label} to={a.href}>
              <Button
                variant="outline"
                className="h-auto min-h-[80px] w-full flex-col gap-2 whitespace-normal px-3 py-4 text-center hover:border-primary/40 hover:bg-primary/5"
              >
                <a.icon className="h-5 w-5 shrink-0 text-primary" />
                <span className="w-full text-xs font-medium leading-snug">{a.label}</span>
              </Button>
            </Link>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
